import { signal } from '@preact/signals'

/**
 * Установка на домашний экран.
 *
 * Браузер предлагает её сам, но ровно один раз: отказался или пропустил — и предложение
 * больше не появится. Поэтому событие перехватывается и придерживается, а человек может
 * вернуться к установке кнопкой в любой момент.
 */

interface InstallPrompt extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let saved: InstallPrompt | null = null

/** Можно ли предложить установку прямо сейчас — браузер дал такое право. */
export const canInstall = signal(false)

/**
 * iPhone и iPad. Safari не умеет предлагать установку из кода вообще: у Apple нет
 * такой возможности, установка делается руками через «Поделиться». Там же кроется
 * вторая половина: уведомления на iOS работают только у приложения, добавленного
 * на домашний экран, а не у страницы в браузере.
 */
export function isIos(): boolean {
  const ua = globalThis.navigator?.userAgent ?? ''
  const iPadOnMac = /Macintosh/.test(ua) && (globalThis.navigator?.maxTouchPoints ?? 0) > 1
  return /iPhone|iPad|iPod/.test(ua) || iPadOnMac
}

/** Уже установлено: запущено с домашнего экрана или браузер сообщил об установке. */
export const installed = signal(isStandalone())

/** На этой площадке установку показывают руками: кнопка ведёт к подсказке, а не к окну браузера. */
export const needsManualInstall = signal(isIos() && !isStandalone())

function isStandalone(): boolean {
  return (
    globalThis.matchMedia?.('(display-mode: standalone)').matches === true ||
    (globalThis.navigator as { standalone?: boolean }).standalone === true
  )
}

export function watchInstall(): void {
  globalThis.addEventListener('beforeinstallprompt', (event) => {
    // Без этого браузер покажет своё предложение один раз и забудет о нём.
    event.preventDefault()
    saved = event as InstallPrompt
    canInstall.value = true
  })

  globalThis.addEventListener('appinstalled', () => {
    saved = null
    canInstall.value = false
    installed.value = true
  })
}

/** Показывает предложение браузера. Возвращает, согласился ли человек. */
export async function promptInstall(): Promise<boolean> {
  if (saved === null) return false
  await saved.prompt()
  const { outcome } = await saved.userChoice
  // Повторно то же самое предложение показать нельзя: браузер выдаёт его по одному разу.
  saved = null
  canInstall.value = false
  return outcome === 'accepted'
}
